import { LoggedError, NamedError } from "./reported-errors";

/**
 * What kind of failure an error is, declared by its {@link TypedError} class (see
 * {@link classifyError}); what remote monitoring does with each is `REPORTING_POLICY`.
 * Foreign errors are converted into typed errors where they enter the app, by the
 * module they belong to, which also defines the typed classes it creates (each
 * documents what its kind of failure is).
 */
export type ErrorClassification =
  | "connectivity"
  | "session-expired"
  | "not-permitted"
  | "local-storage"
  | "server-error"
  | "write-conflict";

/** What a typed error keeps of the foreign failure it was converted from. */
export interface TypedErrorOptions {
  /** the HTTP status of the failed request */
  status?: number;
  /** the original (foreign) error */
  cause?: unknown;
  details?: Record<string, unknown>;
}

/**
 * A failure of a known kind, converted from a foreign error where it entered the
 * app. Each subclass declares its kind once (`static classification`) and is
 * registered with {@link registerTypedError} next to its definition.
 */
export abstract class TypedError extends NamedError {
  declare static readonly classification: ErrorClassification;

  /** The HTTP status of the failed request, if any. */
  readonly status?: number;

  protected constructor(
    name: string,
    message: string,
    options?: TypedErrorOptions,
  ) {
    super(name, message, options);
    this.status = options?.status;
  }
}

/**
 * The classifications by the names of the typed errors, for one that lost its
 * class: a copy made by PouchDB's `createError()`, or an exception of a Sentry
 * event without the original error object.
 */
const TYPED_ERROR_NAMES = new Map<string, ErrorClassification>();

/** Recognize the errors of a typed class also by their name (a literal: class names are minified). */
export function registerTypedError(type: {
  errorName: string;
  classification: ErrorClassification;
}): void {
  TYPED_ERROR_NAMES.set(type.errorName, type.classification);
}

/** Whether the name is that of a typed error, also of one that lost its class. */
export function isTypedErrorName(name: unknown): boolean {
  return TYPED_ERROR_NAMES.has(name as string);
}

/** `DatabaseException` (which depends on this file) has the classification of the typed error it wraps. */
const DATABASE_EXCEPTION_NAME = "DatabaseException";

/**
 * What kind of failure the given error is, judged by this one error (not its
 * `cause`s): a typed error by its class, or by its name if it lost its class; a
 * `DatabaseException` by the typed error it wraps. Any other error is not
 * classified: foreign errors are converted where they enter the app.
 */
export function classifyError(error: unknown): ErrorClassification | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const typed =
    error instanceof NamedError && error.name === DATABASE_EXCEPTION_NAME
      ? error.cause
      : error;
  if (typed instanceof TypedError) {
    return (typed.constructor as typeof TypedError).classification;
  }
  if (error instanceof LoggedError || error instanceof NamedError) {
    return undefined;
  }
  return TYPED_ERROR_NAMES.get((error as { name?: unknown }).name as string);
}

/**
 * The classification of the first error in the `cause` chain that has one,
 * outermost first: a boundary's typed error wins over the raw error it wraps.
 * @param classify of each error (remote monitoring adds unconverted foreign errors)
 */
export function classifyErrorChain(
  error: unknown,
  classify: (error: unknown) => ErrorClassification | undefined = classifyError,
): ErrorClassification | undefined {
  for (const link of causeChain(error)) {
    const classification = classify(link);
    if (classification) {
      return classification;
    }
  }
  return undefined;
}

/** The error and the `cause`s it wraps, outermost first (cycle-safe, max 6 levels). */
export function causeChain(error: unknown): unknown[] {
  const chain: unknown[] = [];
  let current = error;
  while (
    current &&
    (typeof current === "object" || typeof current === "string") &&
    !chain.includes(current) &&
    chain.length <= 5
  ) {
    chain.push(current);
    current = (current as { cause?: unknown }).cause;
  }
  return chain;
}
